import { motion } from 'framer-motion'
import { 
  Check, 
  ArrowRight, 
  Truck, 
  Calculator,
  AlertTriangle,
  Award
} from 'lucide-react'
import { Link } from 'react-router-dom'

const Pricing = () => {
  const perks = [
    "Kompleksowa obsługa PL / CH / DE",
    "Dokumentacja dwujęzyczna (polsko-niemiecka)",
    "Bezpośredni kontakt telefoniczny & e-mail",
    "Gotowe szablony umów i procedur",
  ]

  const tiers = [
    {
      id: "ckz",
      icon: Award,
      title: "CKZ – Licencja Transportowa",
      subtitle: "Udostępnienie Certyfikatu Kompetencji Zawodowych pod licencję",
      price: "od 300 zł",
      priceNote: "miesięcznie netto na FV",
      cta: "Zamów Udostępnienie CKZ",
      highlight: true,
      features: [
        "Pierwszy pojazd w firmie: 300 zł netto / m-c",
        "Każdy kolejny pojazd: tylko +100 zł netto / m-c",
        "Pełne zgłoszenie certyfikatu pod Twoją licencję",
        "Zgodność z Rozporządzeniem WE 1071/2009",
        "Obowiązkowe dla busów > 2.5 t oraz ciężarówek",
        "Darmowa pomoc przy składaniu wniosków do GITD",
        "Stałe aktualizowanie dokumentów firmy"
      ],
    },
    {
      id: "consulting",
      icon: Truck,
      title: "Doradztwo Transportowe",
      subtitle: "Procedury, kontrole, audyty, compliance ITD / BAG",
      price: "290 zł / h",
      priceNote: "rozliczenie godzinowe netto na FV",
      cta: "Umów Konsultację",
      highlight: false,
      features: [
        "Planowanie tras, stawek za kilometr oraz KPI floty",
        "Weryfikacja umów i regulaminów pracy kierowców",
        "Audyt procedur tachografowych i archiwizacji",
        "Przygotowanie firmy do kontroli drogowych i ITD",
        "Doradztwo w transporcie Szwajcaria ↔ Niemcy"
      ],
    },
    {
      id: "payroll",
      icon: Calculator,
      title: "Rozliczanie Kierowców",
      subtitle: "Płace minimalne, diety, ryczałty i ewidencja czasu pracy",
      price: "od 119 zł",
      priceNote: "miesięcznie netto za kierowcę",
      cta: "Rozpocznij Rozliczenia",
      highlight: false,
      features: [
        "Pobór i analiza danych z plików DDD tachografu",
        "Rozliczanie według przepisów PL / DE (MiLoG) / CH",
        "Generowanie raportów płacowych i diet do księgowości",
        "Powiadomienia o naruszeniach i błędach czasu pracy",
        "Archiwizacja danych telemetrycznych przez 24 m-ce"
      ],
    },
  ]

  return (
    <div className="min-h-screen bg-slate-50/50 overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-850 to-primary-950 text-white py-24 sm:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(2,132,199,0.15),transparent_60%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto space-y-6"
          >
            <span className="text-xs font-extrabold text-secondary-300 uppercase tracking-widest bg-white/5 border border-white/10 py-1.5 px-4 rounded-full">Licencjonowanie & Rozliczenia</span>
            <h1 className="text-4xl md:text-6xl font-extrabold font-display tracking-tight leading-tight">
              Transparentny Cennik & Usługi CKZ
            </h1>
            <p className="text-lg md:text-xl text-primary-200/90 leading-relaxed font-light">
              Udostępnianie certyfikatu pod licencje transportowe, optymalizacja kosztów oraz profesjonalne ewidencjonowanie czasu pracy kierowców.
            </p>
            
            {/* Badges Flow */}
            <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
              {perks.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold bg-white/5 backdrop-blur-md text-primary-100"
                >
                  <Check className="h-3.5 w-3.5 text-secondary-400" /> {p}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CKZ Comprehensive Guide */}
      <section className="section-padding bg-white relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Guide Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-6 text-slate-655"
            >
              <span className="text-xs font-extrabold text-primary-600 uppercase tracking-widest bg-primary-50 py-1.5 px-4 rounded-full">Kompendium Wiedzy</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-display tracking-tight mt-4">
                Czym jest Certyfikat Kompetencji Zawodowych (CKZ)?
              </h2>
              
              <p className="text-base leading-relaxed text-slate-600">
                **CKZ – Certyfikat Kompetencji Zawodowych Przewoźnika Drogowego** to urzędowy dokument zaświadczający, że osoba nim dysponująca posiada kwalifikacje oraz specjalistyczną wiedzę niezbędną do prowadzenia przedsiębiorstwa transportowego. Jest to **bezwzględny wymóg prawny** w Unii Europejskiej oraz Polsce.
              </p>

              <h3 className="text-xl font-bold font-display text-slate-900 pt-2">Kiedy i kto musi posiadać CKZ?</h3>
              <p className="text-sm leading-relaxed text-slate-600">
                Od **21 maja 2022 roku** obowiązek ten dotyczy również przedsiębiorców wykonujących międzynarodowy zarobkowy transport pojazdami o **DMC powyżej 2,5 tony** (tzw. busy do przewozu towarów).
              </p>

              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-sm text-slate-800 uppercase tracking-wider mb-2">🚚 Transport Rzeczy</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Wymagany przy pojazdach / zestawach o DMC &gt; 3.5 t (krajowy) oraz &gt; 2.5 t (międzynarodowy).
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-800 uppercase tracking-wider mb-2">🧍‍♂️ Transport Osób</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Wymagany przy przewozie osób pojazdami konstrukcyjnie przystosowanymi do przewozu więcej niż 9 osób (łącznie z kierowcą).
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-bold font-display text-slate-900 pt-2">Brak Certyfikatu w Firmie Skutkuje:</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
                  Odmową wydania licencji wspólnotowej
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
                  Brakiem możliwości dopisania pojazdów
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
                  Karami w wysokości do kilkunastu tysięcy złotych
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></span>
                  Utratą dobrej reputacji przewoźnika
                </li>
              </ul>
            </motion.div>
            
            {/* Co oferujemy Box */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-600/5 rounded-full blur-2xl"></div>
                <h3 className="text-2xl font-bold font-display text-slate-900 mb-6">W ramach udostępnienia CKZ otrzymujesz:</h3>
                
                <ul className="space-y-4 text-sm text-slate-600">
                  <li className="flex items-start space-x-3">
                    <span className="text-emerald-500 font-bold shrink-0 mt-0.5">✓</span>
                    <span>Wyznaczenie **certyfikowanego zarządzającego transportem** do zgłoszenia w Biurze ds. Transportu Międzynarodowego (GITD).</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-emerald-500 font-bold shrink-0 mt-0.5">✓</span>
                    <span>Weryfikację dokumentacji i poprawności oświadczeń wymaganych przez polskie i europejskie prawo.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-emerald-500 font-bold shrink-0 mt-0.5">✓</span>
                    <span>Pomoc w przygotowaniu wniosku o **uzyskanie / rozszerzenie licencji transportowej**.</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-emerald-500 font-bold shrink-0 mt-0.5">✓</span>
                    <span>Stałe wsparcie merytoryczne przy wdrożeniach regulacji czasu pracy oraz taryfikatorów kar.</span>
                  </li>
                </ul>
                
                <div className="mt-8 bg-primary-50 rounded-2xl p-5 border border-primary-100">
                  <h4 className="font-bold text-primary-950 text-sm mb-1 leading-tight">Proste Zasady Rozliczeń:</h4>
                  <p className="text-xs text-primary-850 leading-relaxed">
                    Opłata abonamentowa pobierana co miesiąc na podstawie przejrzystej faktury VAT. Brak ukrytych kosztów ani długoterminowych zobowiązań.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Plans Grid */}
      <section className="section-padding bg-slate-50 relative border-t border-slate-100">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <span className="text-xs font-extrabold text-secondary-600 uppercase tracking-widest bg-secondary-50 py-1.5 px-4 rounded-full">Plany Taryfowe</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 font-display tracking-tight mt-4 mb-4">
              Nasze Ceny Usług
            </h2>
            <p className="text-slate-500 leading-relaxed text-lg">
              Wybierz pakiet optymalny dla Twojej firmy. Oferujemy elastyczne fakturowanie w PLN, CHF lub EUR na życzenie klienta.
            </p>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {tiers.map((t, index) => {
              const Icon = t.icon
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`bg-white rounded-3xl overflow-hidden shadow-lg border transition-all duration-300 relative flex flex-col justify-between ${
                    t.highlight 
                      ? 'border-2 border-accent-gold shadow-2xl scale-100 lg:scale-[1.03] lg:z-10' 
                      : 'border-slate-100 hover:border-slate-200 hover:shadow-xl'
                  }`}
                >
                  {/* Bestseller Badge */}
                  {t.highlight && (
                    <span className="absolute top-5 right-5 text-[10px] font-extrabold uppercase tracking-widest bg-accent-gold text-slate-900 px-3.5 py-1 rounded-full">
                      Zalecany Pakiet
                    </span>
                  )}
                  
                  {/* Top content */}
                  <div className="p-8">
                    <div className="flex items-center gap-3.5 mb-6">
                      <div className={`p-3 rounded-2xl shrink-0 ${t.highlight ? 'bg-amber-50 text-accent-gold' : 'bg-primary-50 text-primary-600'}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Usługa Biznesowa</span>
                    </div>
                    
                    <h3 className="text-xl font-extrabold text-slate-900 font-display mb-2">{t.title}</h3>
                    <p className="text-slate-500 text-xs leading-relaxed mb-6 min-h-[36px]">{t.subtitle}</p>
                    
                    {/* Price Block */}
                    <div className="bg-slate-50/80 rounded-2xl p-5 border border-slate-100 mb-6">
                      <div className="text-3xl font-extrabold text-slate-900 font-display leading-tight">{t.price}</div>
                      <div className="text-[11px] font-semibold text-slate-450 uppercase tracking-wide mt-1 leading-snug">{t.priceNote}</div>
                    </div>
                    
                    {/* Features */}
                    <div className="space-y-4">
                      <h4 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">W cenie pakietu:</h4>
                      <ul className="space-y-3">
                        {t.features.map((f, fIdx) => (
                          <li key={fIdx} className="flex gap-2.5 items-start text-xs text-slate-600 leading-normal">
                            <Check className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Action CTA */}
                  <div className="p-8 bg-slate-50/50 border-t border-slate-100">
                    <Link 
                      to="/contact" 
                      className={`w-full font-bold py-3.5 px-6 rounded-xl transition-all duration-200 active:scale-95 flex items-center justify-center ${
                        t.highlight 
                          ? 'bg-accent-gold hover:bg-amber-600 text-slate-900 shadow-md shadow-amber-500/10' 
                          : 'bg-primary-600 hover:bg-primary-750 text-white shadow-md'
                      }`}
                    >
                      <span>{t.cta}</span>
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="section-padding bg-white relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-12 text-center md:text-left"
          >
            <span className="text-xs font-extrabold text-secondary-600 uppercase tracking-widest bg-secondary-50 py-1.5 px-4 rounded-full">Oferty Komplementarne</span>
            <h3 className="text-3xl font-extrabold text-slate-900 font-display tracking-tight mt-4 mb-2">Usługi Dodatkowe</h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 flex flex-col justify-between card-interactive">
              <div>
                <h4 className="text-lg font-bold font-display text-slate-900 mb-3">Wdrożenie Tachografów</h4>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">Wzorcowe procedury odczytu, ewidencjonowanie urlopówek, szkolenia kierowców z trybów out i promów.</p>
              </div>
              <p className="text-sm font-bold text-primary-600 mt-2">od 890 zł jednorazowo</p>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 flex flex-col justify-between card-interactive">
              <div>
                <h4 className="text-lg font-bold font-display text-slate-900 mb-3">Pakiet Flotowy 10+</h4>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">Stała dedykowana obsługa zarządzającego transportem, kwartalne audyty oraz zniżki na rozliczenia ewidencji.</p>
              </div>
              <p className="text-sm font-bold text-primary-600 mt-2">-10% zniżki abonamentowej</p>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 flex flex-col justify-between card-interactive">
              <div>
                <h4 className="text-lg font-bold font-display text-slate-900 mb-3">Szkolenia Wewnętrzne</h4>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">2-3 godzinne profesjonalne panele szkoleniowe online na żywo: zasady kontroli ITD / BAG, czas jazdy, taryfikatory.</p>
              </div>
              <p className="text-sm font-bold text-primary-600 mt-2">690 zł za sesję / firma</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimers & Alert Box */}
      <section className="section-padding bg-slate-50 relative border-t border-slate-100">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 border-l-4 border-l-amber-500 bg-white shadow-md">
            <div className="flex gap-4 items-start">
              <div className="bg-amber-100 p-2.5 rounded-xl text-amber-600 shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="space-y-3">
                <h3 className="text-xl font-bold font-display text-slate-900">Zastrzeżenia prawne i operacyjne:</h3>
                <div className="text-xs text-slate-500 space-y-2 leading-relaxed">
                  <p>
                    • Wszystkie ceny podane w cennikach mają charakter informacyjny i stanowią ceny netto. Do usług świadczonych na terenie Rzeczypospolitej Polskiej doliczany jest podatek VAT (23%).
                  </p>
                  <p>
                    • Rozliczanie czasu pracy kierowców realizujemy na podstawie przesyłanych plików cyfrowych tachografu (DDD) i odczytów GPS. Klient ponosi pełną odpowiedzialność za terminowość przesyłania plików oraz rzetelność danych kadrowo-płacowych.
                  </p>
                  <p>
                    • Świadczone przez nas usługi doradcze opierają się na aktualnej wykładni przepisów prawa transportowego, nie stanowią jednak porady prawnej w rozumieniu Ustawy o radcach prawnych. W razie skomplikowanych sporów sądowych współpracujemy z wyspecjalizowanymi kancelariami.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Bottom Banner */}
      <section className="section-padding bg-gradient-to-br from-primary-900 to-primary-950 text-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(2,132,199,0.12),transparent_70%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <h2 className="text-3xl md:text-5xl font-extrabold font-display tracking-tight leading-tight">
              Potrzebujesz Certyfikatu CKZ?
            </h2>
            <p className="text-lg md:text-xl text-primary-200/90 leading-relaxed font-light">
              Napisz lub zadzwoń do nas, aby omówić zasady udostępnienia i zawnioskować o licencję. Oferujemy ekspresowy czas reakcji.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Link to="/contact" className="btn-secondary text-sm px-8 py-4 inline-flex items-center justify-center">
                Darmowa wycena i konsultacja
              </Link>
              <a href="tel:+41762373301" className="btn-outline text-sm px-8 py-4 inline-flex items-center justify-center">
                Zadzwoń do nas
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Pricing
